import { z } from 'zod/v4';

import {
    advancedBorderSettingsSchema,
    advancedRadiusSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    shadowSettingsSchemaLegacy,
    themeButtonDataSchemaLegacy,
    themeButtonHoverSchemaLegacy,
} from '../../../common/schemas/legacy';

// The login-edu widget's modal triggers are plain `FormButtonSettings` bags —
// the same shape the subscription-form and payment-form widgets submit with,
// driven by the same `sp-form-button-settings` editor. Reuse the form widget's
// schema rather than duplicating it.
import { formButtonSettingsSchema } from '../../../form/schemas/legacy/form-settings';

/**
 * Link target of a `default` (free-form) button — mirrors `ButtonLink`
 * (`apps/websites/src/app/widgets/login-edu/type/login-edu-widget.type.ts`).
 */
export const loginEduButtonLinkSchema = z.object({
    url: z.string().nullish(),
    type: z.enum(['email', 'phone', 'link', 'anchor']).nullish(),
});

/**
 * Button that opens the auth modal — `LoginEduFormTrigger`, i.e. the shared
 * `FormButtonSettings` bag plus the widget's own `isActive` flag.
 */
export const loginEduFormTriggerSchema = formButtonSettingsSchema.extend({
    isActive: z.boolean(),
});

/**
 * The `ThemedBaseButton` half every login-edu button carries: style preset plus
 * the base/active/hover theme bags and the shadow pair.
 */
const themedBaseButtonShape = {
    styleType: buttonStyleTypeSchema,
    buttonBase: themeButtonDataSchemaLegacy,
    buttonActive: themeButtonDataSchemaLegacy.nullish(),
    buttonHover: themeButtonHoverSchemaLegacy.nullish(),
    isShadow: z.boolean().nullish(),
    shadowSettings: shadowSettingsSchemaLegacy.nullish(),
} as const;

/** Corner/border overrides shared by all three button variants. */
const buttonBorderShape = {
    isIndependentCorners: z.boolean().nullish(),
    advancedRadius: advancedRadiusSchema.nullish(),
    advancedBorderSettings: advancedBorderSettingsSchema.nullish(),
} as const;

/**
 * Inner button of the registration group (`LoginButtonData`) — a themed button
 * with a label and size, but no link and no discriminator of its own. The host
 * seeds only `label`/`styleType`/`size`/`buttonBase` for the enroll and enter
 * buttons, so the rest of the themed bag stays `nullish()`.
 */
export const loginEduInnerButtonSchema = z.object({
    ...themedBaseButtonShape,
    ...buttonBorderShape,
    label: z.string().nullable(),
    size: buttonSizeSchema.nullable(),
    isActive: z.boolean().nullish(),
});

/**
 * Free-form button placed next to the auth buttons — carries its own link.
 */
export const loginEduDefaultButtonSchema = z.object({
    ...themedBaseButtonShape,
    ...buttonBorderShape,
    buttonType: z.literal('default'),
    label: z.string().nullable(),
    size: buttonSizeSchema.nullable(),
    link: loginEduButtonLinkSchema,
    isTargetBlank: z.boolean().nullish(),
    isActive: z.boolean().nullish(),
});

/**
 * "Log in" button — a themed button that opens the sign-in form, plus the
 * separately styled `modalTrigger` shown when `formView` is `modal`.
 */
export const loginEduAuthorizationButtonSchema = z.object({
    ...themedBaseButtonShape,
    ...buttonBorderShape,
    buttonType: z.literal('authorization'),
    label: z.string().nullable(),
    size: buttonSizeSchema.nullable(),
    isActive: z.boolean(),
    modalTrigger: loginEduFormTriggerSchema,
});

/**
 * "Sign up" group — one entry rendering three different inner buttons
 * depending on the visitor's state (register / enroll / already enrolled),
 * sharing one theme bag and one modal trigger.
 */
export const loginEduRegistrationButtonSchema = z.object({
    ...buttonBorderShape,
    buttonType: z.literal('registration'),
    isActive: z.boolean(),
    registrationButton: loginEduInnerButtonSchema,
    enrollButton: loginEduInnerButtonSchema,
    enterButton: loginEduInnerButtonSchema,
    buttonBase: themeButtonDataSchemaLegacy,
    buttonHover: themeButtonHoverSchemaLegacy.nullish(),
    isShadow: z.boolean().nullish(),
    shadowSettings: shadowSettingsSchemaLegacy.nullish(),
    modalTrigger: loginEduFormTriggerSchema,
});

/**
 * `data.buttons[]` — discriminated on `buttonType`, mirroring
 * `LoginWidgetButtons`.
 */
export const loginEduButtonSchema = z.discriminatedUnion('buttonType', [
    loginEduDefaultButtonSchema,
    loginEduAuthorizationButtonSchema,
    loginEduRegistrationButtonSchema,
]);

export type LoginEduButtonLinkLegacy = z.infer<typeof loginEduButtonLinkSchema>;
export type LoginEduFormTriggerLegacy = z.infer<typeof loginEduFormTriggerSchema>;
export type LoginEduInnerButtonLegacy = z.infer<typeof loginEduInnerButtonSchema>;
export type LoginEduDefaultButtonLegacy = z.infer<typeof loginEduDefaultButtonSchema>;
export type LoginEduAuthorizationButtonLegacy = z.infer<typeof loginEduAuthorizationButtonSchema>;
export type LoginEduRegistrationButtonLegacy = z.infer<typeof loginEduRegistrationButtonSchema>;
export type LoginEduButtonLegacy = z.infer<typeof loginEduButtonSchema>;
